import { useState } from "react";
import { Button } from "./Button";
import { Alert, Card, EmptyState } from "./Card";
import { Field, Input, Select } from "./Field";
import { StatusPill } from "./Status";
import type { Issue, Milestone } from "../lib/tasks";
import { errText, isWorking, tasksApi } from "../lib/tasks";
import type { Project } from "../lib/project";
import { tx } from "../i18n";

/* ══ 마일스톤 화면의 조각(t-in-port-work-milestones) ══
   폼 · 이슈 체크 목록 · 넣기/빼기 막대. 막대는 공통 .b-bulkbar, 폼은 .b-field/.b-input, 목록은 .b-dt + .b-check. */

type MsApi = Pick<typeof tasksApi, "createMilestone" | "setMilestone">;

/** 새 마일스톤 폼 — id 는 비워도 된다(서버가 만든다). 사인오프 칸은 없다(b3x-in 마일스톤에 그 칸이 없다). */
export function MilestoneForm({
  projects,
  defaultProject = "",
  onCancel,
  onCreated,
  api = tasksApi,
  initialError,
}: {
  projects: Project[];
  defaultProject?: string;
  onCancel: () => void;
  onCreated: (m: Milestone) => void;
  api?: MsApi;
  initialError?: string;
}) {
  const [id, setId] = useState("");
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [project, setProject] = useState(defaultProject);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(initialError ?? null);
  const idBad = id !== "" && !/^[a-z0-9][a-z0-9-]*$/.test(id);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const m = await api.createMilestone({
        id: id || undefined,
        title: title.trim(),
        goal: goal.trim() || undefined,
        projectId: project || undefined,
      });
      onCreated(m);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={tx("milestones.form.title")}>
      <form
        className="msform"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim() && !idBad) void submit();
        }}
      >
        <Field label={tx("milestones.form.name")}>
          {(fid) => <Input id={fid} value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />}
        </Field>
        <Field label={tx("milestones.form.id")} hint={tx("milestones.form.idHint")} error={idBad ? tx("milestones.form.idBad") : undefined}>
          {(fid, invalid) => <Input id={fid} value={id} invalid={invalid} onChange={(e) => setId(e.target.value.trim())} placeholder="m1-core" />}
        </Field>
        <Field label={tx("milestones.form.goal")}>
          {(fid) => <textarea id={fid} className="b-textarea" rows={2} value={goal} onChange={(e) => setGoal(e.target.value)} />}
        </Field>
        <Field label={tx("milestones.form.project")}>
          {(fid) => (
            <Select id={fid} value={project} onChange={(e) => setProject(e.target.value)}>
              <option value="">{tx("dashx.projects.none")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {err ? <Alert tone="error">{tx("milestones.form.failed", { err })}</Alert> : null}
        <div className="msform__acts">
          <Button type="button" onClick={onCancel} disabled={busy}>
            {tx("common.action.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!title.trim() || idBad}>
            {tx("milestones.form.create")}
          </Button>
        </div>
      </form>
    </Card>
  );
}

const CLOSED = new Set(["done", "cancelled"]);

/** 이슈 체크 목록 — 두 무리: 이 마일스톤의 이슈 · 넣을 수 있는 이슈(마일스톤 없음 · 같은 프로젝트 · 열린 것).
 *  다른 마일스톤에 든 이슈는 후보에 안 올린다 — 여기서 넣으면 그 마일스톤에서 말없이 빠진다. */
export function MilestoneIssueList({
  ms,
  list,
  project,
  picked,
  onPick,
}: {
  ms: Milestone;
  list: Issue[];
  project: string | null;
  picked: Set<string>;
  onPick: (id: string, on: boolean) => void;
}) {
  const inMs = list.filter((t) => t.milestoneId === ms.id);
  const cand = list.filter(
    (t) => !t.milestoneId && !CLOSED.has(t.status) && t.childCount === 0 && (!project || (t.projectId ?? null) === project),
  );
  return (
    <div className="mslist">
      <Card title={tx("milestones.inMs", { n: inMs.length })}>
        {inMs.length ? <Rows rows={inMs} picked={picked} onPick={onPick} /> : <EmptyState compact icon="flag" title={tx("milestones.inMsEmpty")} />}
      </Card>
      <Card title={tx("milestones.cand", { n: cand.length })} meta={project ? null : tx("milestones.candAll")}>
        {cand.length ? <Rows rows={cand} picked={picked} onPick={onPick} /> : <EmptyState compact icon="check" title={tx("milestones.candEmpty")} />}
      </Card>
    </div>
  );
}

function Rows({ rows, picked, onPick }: { rows: Issue[]; picked: Set<string>; onPick: (id: string, on: boolean) => void }) {
  return (
    <div className="b-dt mslist__dt">
      {rows.map((t) => (
        <label key={t.id} className={`b-dt-row is-interactive mslist__row${picked.has(t.id) ? " is-selected" : ""}`}>
          <span className="b-check">
            <input type="checkbox" checked={picked.has(t.id)} onChange={(e) => onPick(t.id, e.target.checked)} aria-label={t.title} />
          </span>
          <span className="b-dt-cell is-mono mslist__id">{t.id}</span>
          <span className="b-dt-cell is-primary mslist__title">{t.title}</span>
          <span className="b-dt-cell mslist__st">
            <StatusPill status={t.status} working={isWorking(t)} />
          </span>
        </label>
      ))}
    </div>
  );
}

/** 넣기/빼기 막대 — 고른 것 중 **마일스톤 밖의 것은 넣기, 안의 것은 빼기**로 센다. 하나가 거절돼도 나머지는 간다. */
export function MilestoneBulkBar({
  ms,
  picked,
  onDone,
  onClear,
  api = tasksApi,
}: {
  ms: Milestone;
  picked: Issue[];
  onDone: (okIds: string[]) => void;
  onClear: () => void;
  api?: MsApi;
}) {
  const [busy, setBusy] = useState<"in" | "out" | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const toAdd = picked.filter((t) => t.milestoneId !== ms.id);
  const toDrop = picked.filter((t) => t.milestoneId === ms.id);

  async function each(kind: "in" | "out", rows: Issue[]) {
    setBusy(kind);
    setResult(null);
    const ok: string[] = [];
    const why = new Map<string, number>();
    for (const t of rows) {
      try {
        await api.setMilestone(t.id, kind === "in" ? ms.id : null);
        ok.push(t.id);
      } catch (e) {
        const k = errText(e);
        why.set(k, (why.get(k) ?? 0) + 1);
      }
    }
    const fail = rows.length - ok.length;
    setResult(
      fail === 0
        ? tx("boardSel.result", { ok: ok.length })
        : tx("boardSel.resultFail", { ok: ok.length, fail, why: [...why].map(([k, n]) => (n > 1 ? `${k} ×${n}` : k)).join(" / ") }),
    );
    setBusy(null);
    onDone(ok);
  }

  if (!picked.length && !result) return null;
  return (
    <div className="b-bulkbar" role="region" aria-label={tx("boardSel.count", { n: picked.length })}>
      <div className="b-bulkbar-row">
        <strong>{tx("boardSel.count", { n: picked.length })}</strong>
        <Button size="sm" variant="primary" loading={busy === "in"} disabled={busy !== null || !toAdd.length} onClick={() => each("in", toAdd)}>
          {tx("milestones.add", { n: toAdd.length })}
        </Button>
        <Button size="sm" loading={busy === "out"} disabled={busy !== null || !toDrop.length} onClick={() => each("out", toDrop)}>
          {tx("milestones.drop", { n: toDrop.length })}
        </Button>
        <span className="b-bulkbar-gap" />
        <Button size="sm" variant="ghost" onClick={onClear} disabled={busy !== null}>
          {tx("boardSel.clear")}
        </Button>
      </div>
      {result ? <p className="b-bulkbar-result">{result}</p> : null}
    </div>
  );
}
